import React, { useEffect, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { isoColors, planeBasis } from '../lib/iso-scene';

/** Turns on per-material clipping planes (renderer.localClippingEnabled). */
export function EnableClipping() {
  const { gl } = useThree();
  useEffect(() => {
    const prev = gl.localClippingEnabled;
    gl.localClippingEnabled = true;
    return () => { gl.localClippingEnabled = prev; };
  }, [gl]);
  return null;
}

/** Iso-surface mesh (scene frame) coloured by level or depth, with opacity and clipping. */
export function IsoSurfaceMesh({ mesh, colorMode, levelT, opacity, clippingPlanes }) {
  const geometry = useMemo(() => {
    if (!mesh) return null;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3));
    g.setAttribute('color', new THREE.BufferAttribute(isoColors(mesh, colorMode, levelT), 3));
    g.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    return g;
  }, [mesh, colorMode, levelT]);
  useEffect(() => () => { if (geometry) geometry.dispose(); }, [geometry]);
  if (!geometry) return null;
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial vertexColors transparent={opacity < 1} opacity={opacity} side={THREE.DoubleSide}
        roughness={0.6} metalness={0.05} clippingPlanes={clippingPlanes} />
    </mesh>
  );
}

/**
 * Cutting-plane gizmo: an outline of the plane and a normal arrow, plus the resampled model slice as a
 * textured quad lying on the plane. `slice` is { data: Uint8Array RGBA, res } from lib/iso-scene.planeSlice.
 */
export function CuttingPlaneGizmo({ centre, normal, size, slice }) {
  const quat = useMemo(() => {
    const { u, v } = planeBasis(normal);
    const m = new THREE.Matrix4().makeBasis(new THREE.Vector3(...u), new THREE.Vector3(...v), new THREE.Vector3(...normal));
    return new THREE.Quaternion().setFromRotationMatrix(m);
  }, [normal]);

  const texture = useMemo(() => {
    if (!slice) return null;
    const t = new THREE.DataTexture(slice.data, slice.res, slice.res, THREE.RGBAFormat);
    t.magFilter = THREE.NearestFilter;
    t.needsUpdate = true;
    return t;
  }, [slice]);
  useEffect(() => () => { if (texture) texture.dispose(); }, [texture]);

  const outline = useMemo(() => {
    const h = size / 2;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-h, -h, 0, h, -h, 0, h, h, 0, -h, h, 0], 3));
    return g;
  }, [size]);
  useEffect(() => () => outline.dispose(), [outline]);

  const arrowTip = Math.max(2, size * 0.08);
  return (
    <group position={centre} quaternion={quat}>
      <lineLoop geometry={outline}><lineBasicMaterial color="#fbbf24" /></lineLoop>
      {texture && (
        <mesh>
          <planeGeometry args={[size, size]} />
          <meshBasicMaterial map={texture} transparent side={THREE.DoubleSide} />
        </mesh>
      )}
      <mesh position={[0, 0, arrowTip]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[arrowTip * 0.35, arrowTip, 12]} />
        <meshBasicMaterial color="#fbbf24" />
      </mesh>
    </group>
  );
}
